import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Loader2, Bot, User, Mail } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ReactMarkdown from "react-markdown";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatbotSAVProps {
  trigger?: React.ReactNode;
  buttonText?: string;
  buttonClassName?: string;
  showIcon?: boolean;
}

const CHAT_URL = "https://tpujpzojoqwytuhgbwtw.supabase.co/functions/v1/chatbot-sav";
const SAVE_URL = "https://tpujpzojoqwytuhgbwtw.supabase.co/functions/v1/save-chatbot-conversation";

// Generate a unique session ID
const generateSessionId = () => {
  return crypto.randomUUID();
};

// Patterns to detect when bot asks for email
const EMAIL_ASK_PATTERNS = [
  "donner l'email",
  "donner ton email",
  "donner votre email",
  "me donner l'email",
  "communiquer ton email",
  "communiquer votre email",
  "fournir ton email",
  "fournir votre email",
  "adresse email",
  "adresse mail",
  "email utilisé",
  "email de la commande"
];

// Check if the last bot message asks for email
const botAsksForEmail = (messages: Message[]): boolean => {
  const lastBotMessage = [...messages].reverse().find(m => m.role === "assistant");
  if (!lastBotMessage) return false;
  const content = lastBotMessage.content.toLowerCase();
  return EMAIL_ASK_PATTERNS.some(pattern => content.includes(pattern));
};

// Validate email format
const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const QUICK_REPLIES = [
  { label: "🔑 Connexion", message: "J'ai un problème de connexion" },
  { label: "📲 Installation", message: "Comment installer sur mon appareil ?" },
  { label: "🎥 Chaînes", message: "Quelles chaînes sont disponibles ?" },
  { label: "💳 Paiement", message: "Comment fonctionne le paiement ?" },
  { label: "🎁 Offres", message: "Quelles sont vos offres ?" },
  { label: "📩 Support", message: "J'ai besoin de parler au support" },
];

const ChatbotSAV = ({ 
  trigger, 
  buttonText = "Vous avez des questions ?", 
  buttonClassName, 
  showIcon = true 
}: ChatbotSAVProps) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { 
      role: "assistant", 
      content: "👋 Salut, moi c'est Alex du support RoiduStreaming.\nBesoin d'aide ?" 
    }
  ]);
  const [input, setInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  
  // Check if we should show email input
  const showEmailInput = botAsksForEmail(messages) && !userEmail && !isLoading;

  // Save conversation to database (debounced)
  const saveConversation = useCallback(async (msgs: Message[], email?: string | null) => {
    // Filter out empty messages
    const validMessages = msgs.filter(m => m.content.trim() !== "");
    if (validMessages.length <= 1) return; // Don't save if only greeting

    try {
      await fetch(SAVE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRwdWpwem9qb3F3eXR1aGdid3R3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMjc3ODIsImV4cCI6MjA4MjYwMzc4Mn0.FWoxjkrmUcPA918-nyew0OsO5xq_fP9HWHOYfNd2U0E`,
        },
        body: JSON.stringify({
          sessionId,
          messages: validMessages,
          userEmail: email || userEmail,
        }),
      });
    } catch (error) {
      console.error("Error saving conversation:", error);
    }
  }, [sessionId, userEmail]);

  // Handle email submission
  const handleEmailSubmit = () => {
    const email = emailInput.trim().toLowerCase();
    if (!isValidEmail(email)) {
      return;
    }
    
    setUserEmail(email);
    // Send email as user message
    sendMessage(email);
    setEmailInput("");
  };

  // Focus email input when it appears
  useEffect(() => {
    if (showEmailInput && emailInputRef.current) {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    }
  }, [showEmailInput]);

  // Debounced save - save 2 seconds after last message
  const debouncedSave = useCallback((msgs: Message[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveConversation(msgs);
    }, 2000);
  }, [saveConversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const sendMessage = async (messageText?: string) => {
    const textToSend = messageText || input.trim();
    if (!textToSend || isLoading) return;

    const userMessage: Message = { role: "user", content: textToSend };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    setShowQuickReplies(false);

    let assistantContent = "";

    try {
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRwdWpwem9qb3F3eXR1aGdid3R3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMjc3ODIsImV4cCI6MjA4MjYwMzc4Mn0.FWoxjkrmUcPA918-nyew0OsO5xq_fP9HWHOYfNd2U0E`,
        },
        body: JSON.stringify({ 
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          sessionId
        }),
      });

      if (!resp.ok || !resp.body) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.error || "Erreur de connexion");
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      // Add empty assistant message to start streaming into
      setMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const updated = [...prev];
                if (updated[updated.length - 1]?.role === "assistant") {
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                }
                return updated;
              });
            }
          } catch {
            // Incomplete JSON, continue
          }
        }
      }

      // Process remaining buffer
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split("\n")) {
          if (!raw) continue;
          if (raw.endsWith("\r")) raw = raw.slice(0, -1);
          if (raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantContent += content;
              setMessages(prev => {
                const updated = [...prev];
                if (updated[updated.length - 1]?.role === "assistant") {
                  updated[updated.length - 1] = { role: "assistant", content: assistantContent };
                }
                return updated;
              });
            }
          } catch { /* ignore */ }
        }
      }

      // Save conversation after receiving response
      setMessages(prev => {
        const finalMessages = prev.filter(m => m.content !== "");
        debouncedSave(finalMessages);
        return prev;
      });

    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => {
        const errorMessages = [
          ...prev.filter(m => m.content !== ""),
          { 
            role: "assistant" as const, 
            content: "Désolé, je rencontre un problème technique. Vous pouvez nous contacter directement par email." 
          }
        ];
        debouncedSave(errorMessages);
        return errorMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const defaultTrigger = (
    <button
      className={buttonClassName || "fixed bottom-5 right-5 px-4 py-3 min-h-[48px] rounded-full flex items-center justify-center gap-2 z-50 transition-all duration-300 hover:scale-110 shadow-lg shadow-primary/25 bg-primary hover:bg-primary/90 animate-[bounce_2s_ease-in-out_3]"}
      aria-label="Ouvrir le chatbot d'assistance"
    >
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
      </span>
      <span className="text-primary-foreground font-medium text-sm whitespace-nowrap">
        💬 Une question ? On vous répond !
      </span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md h-[80vh] max-h-[600px] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b bg-primary/5">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-primary-foreground text-sm font-bold">
              A
            </div>
            Alex — Support RoiduStreaming
          </DialogTitle>
        </DialogHeader>

        {/* Messages Area */}
        <ScrollArea className="flex-1 px-4 py-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <Avatar className={`w-8 h-8 ${msg.role === "user" ? "bg-primary" : "bg-gradient-to-br from-primary to-primary/70"}`}>
                  <AvatarFallback className={msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-gradient-to-br from-primary to-primary/70 text-primary-foreground font-bold"}>
                    {msg.role === "user" ? <User className="w-4 h-4" /> : "A"}
                  </AvatarFallback>
                </Avatar>
                <div
                  className={`rounded-2xl px-4 py-2 max-w-[80%] ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-muted rounded-bl-md"
                  }`}
                >
                  {msg.role === "user" ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>ul]:my-1 [&>ol]:my-1 [&>li]:my-0.5 [&>p>a]:text-primary [&>p>a]:underline [&>p>a]:hover:opacity-80 [&_a]:text-primary [&_a]:underline [&_a]:hover:opacity-80">
                      <ReactMarkdown
                        components={{
                          a: ({ href, children }) => (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:opacity-80">
                              {children}
                            </a>
                          ),
                        }}
                      >{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.content === "" && (
              <div className="flex gap-3">
                <Avatar className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70">
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground font-bold">
                    A
                  </AvatarFallback>
                </Avatar>
                <div className="rounded-2xl px-4 py-2 bg-muted rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              </div>
            )}
            {/* Quick Replies */}
            {showQuickReplies && messages.length === 1 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {QUICK_REPLIES.map((reply) => (
                  <button
                    key={reply.label}
                    onClick={() => sendMessage(reply.message)}
                    className="px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-full transition-colors"
                  >
                    {reply.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t bg-background space-y-3">
          {/* Email Input - Shows when bot asks for email */}
          {showEmailInput && (
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Mail className="w-4 h-4" />
                <span>Entrez votre email pour que le support puisse vous recontacter :</span>
              </div>
              <div className="flex gap-2">
                <Input
                  ref={emailInputRef}
                  type="email"
                  placeholder="votre.email@exemple.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleEmailSubmit();
                    }
                  }}
                  className="flex-1 bg-background"
                />
                <Button 
                  onClick={handleEmailSubmit}
                  disabled={!isValidEmail(emailInput.trim())}
                  size="sm"
                  className="shrink-0"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Envoyer
                </Button>
              </div>
              {emailInput && !isValidEmail(emailInput.trim()) && (
                <p className="text-xs text-destructive">Veuillez entrer un email valide</p>
              )}
            </div>
          )}

          {/* Regular text input */}
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              placeholder={showEmailInput ? "Ou posez une autre question..." : "Posez votre question..."}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              maxLength={500}
              className="flex-1"
            />
            <Button 
              onClick={() => sendMessage()} 
              disabled={!input.trim() || isLoading}
              size="icon"
              className="shrink-0"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Support disponible 24/7
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChatbotSAV;
